/**
 * Realtime event protocol — shared by the server publisher, the WS gateway, and
 * the browser client.
 *
 * Channels are pub/sub topics (rooms). Events are the payloads published to
 * them. Two flavours:
 *  - "signal" events carry only identifiers; the client reacts by running its
 *    existing delta/fetch query. This keeps a single serialization path and
 *    avoids the published shape drifting from what the read query returns.
 *  - "payload" events carry small, ephemeral state applied directly to the
 *    cache (typing, presence, buddy status, locations).
 */

export const REALTIME_PROTOCOL_VERSION = 1;

// ---- Channel builders --------------------------------------------------------

export const userChannel = (userId: string) => `user:${userId}`;
export const chatChannel = (chatId: number) => `chat:${chatId}`;
export const presenceChannel = (userId: string) => `presence:${userId}`;
export const mvSessionChannel = (sessionId: number) => `mvsession:${sessionId}`;

export type RealtimeChannelKind = "user" | "chat" | "presence" | "mvsession";

const CHANNEL_KINDS: ReadonlySet<RealtimeChannelKind> = new Set([
  "user",
  "chat",
  "presence",
  "mvsession",
]);

export function parseChannel(
  channel: string,
): { kind: RealtimeChannelKind; id: string } | null {
  const idx = channel.indexOf(":");
  if (idx <= 0) return null;
  const kind = channel.slice(0, idx);
  const id = channel.slice(idx + 1);
  if (!id || !CHANNEL_KINDS.has(kind as RealtimeChannelKind)) return null;
  return { kind: kind as RealtimeChannelKind, id };
}

// ---- Event payloads ----------------------------------------------------------

export type RealtimeEvent =
  // connectivity
  | { type: "ping"; t?: number }
  // chat — signals (client refetches its delta query)
  | { type: "chat.message"; chatId: number; messageId?: number }
  | { type: "chat.message.updated"; chatId: number; messageId: number }
  | { type: "chat.members"; chatId: number }
  | { type: "chat.list.touch"; chatId: number }
  // chat — ephemeral payloads (applied directly)
  | {
      type: "chat.typing";
      chatId: number;
      userId: string;
      name?: string | null;
      expiresAt: number;
    }
  | {
      type: "chat.seen";
      chatId: number;
      userId: string;
      messageId: number;
      at: number;
    }
  | {
      // A message reached `userId`'s device (grey double tick). Direct chats
      // announce each delivery; groups only once every member has it.
      type: "chat.delivered";
      chatId: number;
      userId: string;
      messageId: number;
    }
  | {
      // `userId` changed their last-seen setting. Anyone showing their
      // presence refetches it — the server applies the new rule — so a
      // hidden "Active 4h ago" disappears at once, as on WhatsApp.
      type: "chat.presence.settings";
      userId: string;
    }
  | {
      type: "chat.presence";
      userId: string;
      // Omitted by the server: presence:{id} is open to any signed-in user,
      // and a timestamp per heartbeat would let a listener rebuild a last
      // seen the person hid. The event itself means "online now".
      lastActiveAt?: number;
      online: boolean;
    }
  // notifications
  | { type: "notification.new"; notificationId: string }
  | { type: "notification.unread"; count: number }
  // stories — signal only; client refetches canonical tray/read queries
  | { type: "story.tray.touch"; storyId?: number }
  // instants — signal only; client refetches its inbox pile / vault queries
  | { type: "instant.inbox.touch"; instantId?: number }
  // direct-chat buddy state
  | { type: "buddy.status"; targetUserId: string }
  // movement live sessions
  | {
      type: "mv.location";
      sessionId: number;
      userId: string;
      lat: number;
      lng: number;
      speed?: number | null;
      heading?: number | null;
      capturedAt: number;
    }
  | {
      type: "mv.participant";
      sessionId: number;
      userId: string;
      joined: boolean;
      count?: number;
    };

export type RealtimeEventType = RealtimeEvent["type"];

// ---- Wire frames -------------------------------------------------------------

/** Browser → gateway control frame. */
export type RealtimeClientFrame =
  | { op: "sub"; channel: string }
  | { op: "unsub"; channel: string }
  | { op: "ping" };

/** Gateway → browser frame. */
export type RealtimeServerFrame =
  | { channel: string; event: RealtimeEvent & { v?: number } }
  | { system: "hello" | "pong" | "subscribed" | "denied"; channel?: string };
